import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  Users,
  Search,
  AlertTriangle,
  Download,
  FileSpreadsheet,
  GitMerge,
  Star,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DriverCard from '../components/DriverCard';
import DriverFormDialog from '../components/DriverFormDialog';
import ImportResultDialog from '../components/ImportResultDialog';
import MergeDuplicateDriversDialog from '../components/MergeDuplicateDriversDialog';
import { DriverFuelRanking } from '../components/DriverFuelRanking';
import { ScorecardFormDialog } from '../components/ScorecardFormDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyDriver = {
  name: '',
  id_number: '',
  employee_id: '',
  email: '',
  cell_phone_1: '',
  cell_phone_2: '',
  next_of_kin_name: '',
  next_of_kin_phone: '',
  license_number: '',
  license_type: '',
  license_expiry: '',
  license_photo_url: '',
  photo_url: '',
  pdp_number: '',
  pdp_expiry: '',
  status: 'active',
  notes: '',
  assigned_vehicle_id: ''
};

const DriversPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [incidentsByDriver, setIncidentsByDriver] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [formData, setFormData] = useState({ ...emptyDriver });
  const [saving, setSaving] = useState(false);
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [importResultOpen, setImportResultOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [scoreDialog, setScoreDialog] = useState({ open: false, driverId: null });
  const [rankingKey, setRankingKey] = useState(0);

  const fetchDrivers = useCallback(async () => {
    try {
      const [driversData, incidentReport] = await Promise.all([
        api.getDrivers(),
        axios
          .get(`${API_URL}/api/reports/incidents-by-driver`)
          .then((r) => r.data)
          .catch(() => ({ by_driver: [] })),
      ]);
      setDrivers(driversData);
      api.getVehicles().then(setVehicles).catch(() => {});
      setRankingKey((k) => k + 1);
      const map = {};
      (incidentReport.by_driver || []).forEach((d) => {
        if (d.driver_id && d.driver_id !== 'unassigned') {
          map[d.driver_id] = { total: d.total, damage_total: d.damage_total };
        }
      });
      setIncidentsByDriver(map);
    } catch (error) {
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  const handleDownloadTemplate = async (populated) => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/drivers/export/excel?populated=${populated}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fname = populated
        ? `drivers_export_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
        : 'driver_import_template.xlsx';
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(populated ? 'Drivers exported' : 'Template downloaded');
    } catch {
      toast.error('Failed to download file');
    } finally {
      setDownloading(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) {
      toast.error('Please choose an .xlsx file');
      e.target.value = '';
      return;
    }
    setImporting(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(
        `${API_URL}/api/drivers/import/excel`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImportResult(response.data);
      setImportResultOpen(true);
      await fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFormData({ ...emptyDriver });
    setSelectedDriver(null);
  };

  const handleOpenDialog = (driver = null) => {
    if (driver) {
      setSelectedDriver(driver);
      setFormData({
        name: driver.name,
        id_number: driver.id_number || '',
        employee_id: driver.employee_id || '',
        email: driver.email || '',
        cell_phone_1: driver.cell_phone_1 || '',
        cell_phone_2: driver.cell_phone_2 || '',
        next_of_kin_name: driver.next_of_kin_name || '',
        next_of_kin_phone: driver.next_of_kin_phone || '',
        license_number: driver.license_number || '',
        license_type: driver.license_type || '',
        license_expiry: driver.license_expiry || '',
        license_photo_url: driver.license_photo_url || '',
        photo_url: driver.photo_url || '',
        pdp_number: driver.pdp_number || '',
        pdp_expiry: driver.pdp_expiry || '',
        status: driver.status || 'active',
        notes: driver.notes || '',
        assigned_vehicle_id: driver.assigned_vehicle_id || ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter the driver name (all other fields optional)');
      return;
    }
    setSaving(true);
    // Send null instead of empty strings so optional backend fields don't fail validation
    const payload = { ...formData };
    for (const k of ['license_number', 'license_type', 'license_expiry', 'pdp_expiry', 'phone', 'email', 'address']) {
      if (payload[k] === '') payload[k] = null;
    }
    try {
      if (selectedDriver) {
        await api.updateDriver(selectedDriver.id, payload);
        toast.success('Driver updated');
      } else {
        await api.createDriver(payload);
        toast.success('Driver added');
      }
      setDialogOpen(false);
      resetForm();
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save driver');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDriver) return;
    try {
      await api.deleteDriver(selectedDriver.id);
      toast.success('Driver deleted');
      setDeleteDialogOpen(false);
      setSelectedDriver(null);
      fetchDrivers();
    } catch {
      toast.error('Failed to delete driver');
    }
  };

  const filteredDrivers = drivers.filter(d =>
    (d.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.license_number || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const alertCount = drivers.filter(d => d.license_due || d.pdp_due).length;

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-slate-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="drivers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-obsidian tracking-tight">Drivers</h1>
          <p className="text-slate-500 mt-1">Manage driver profiles and licenses</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={importInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportFile}
              className="hidden"
              data-testid="import-drivers-input"
            />
            <Button
              variant="outline"
              onClick={() => handleDownloadTemplate(true)}
              disabled={downloading}
              data-testid="download-drivers-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              data-testid="upload-drivers-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {importing ? 'Uploading…' : 'Upload Excel'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setMergeDialogOpen(true)}
              data-testid="find-duplicate-drivers-btn"
            >
              <GitMerge className="w-4 h-4 mr-2" />
              Find Duplicates
            </Button>
            <Button
              variant="outline"
              onClick={() => setScoreDialog({ open: true, driverId: null })}
              data-testid="score-driver-btn"
            >
              <Star className="w-4 h-4 mr-2" />
              Score Driver
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-gray-700 hover:bg-gray-600 text-white btn-active"
              data-testid="add-driver-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Driver
            </Button>
          </div>
          <button
            type="button"
            onClick={() => handleDownloadTemplate(false)}
            disabled={downloading}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="download-blank-template-link"
          >
            Download blank template
          </button>
        </div>
      </div>

      {/* Alert */}
      {alertCount > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <p className="text-amber-800">
              <span className="font-medium">{alertCount} driver{alertCount > 1 ? 's' : ''}</span> with license/PDP expiring soon
            </p>
          </CardContent>
        </Card>
      )}

      <DriverFuelRanking key={rankingKey} />

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search drivers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-drivers-input"
        />
      </div>

      {/* Drivers Grid */}
      {filteredDrivers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDrivers.map((driver) => (
            <DriverCard
              key={driver.id}
              driver={driver}
              incidentStats={incidentsByDriver[driver.id]}
              onEdit={handleOpenDialog}
              onDelete={(d) => { setSelectedDriver(d); setDeleteDialogOpen(true); }}
              onScore={(d) => setScoreDialog({ open: true, driverId: d.id })}
            />
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="font-heading text-lg font-semibold text-obsidian mb-2">
              {searchTerm ? 'No drivers found' : 'No drivers yet'}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchTerm ? 'Try a different search' : 'Add your first driver to get started'}
            </p>
            {!searchTerm && (
              <Button onClick={() => handleOpenDialog()} className="bg-gray-700 hover:bg-gray-600 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Driver
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <DriverFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        formData={formData}
        setFormData={setFormData}
        selectedDriver={selectedDriver}
        saving={saving}
        onSubmit={handleSubmit}
        vehicles={vehicles}
      />

      <ScorecardFormDialog
        open={scoreDialog.open}
        onOpenChange={(o) => setScoreDialog((s) => ({ ...s, open: o }))}
        driverId={scoreDialog.driverId}
        onSaved={fetchDrivers}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Driver</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedDriver?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-driver-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImportResultDialog
        open={importResultOpen}
        onOpenChange={setImportResultOpen}
        result={importResult}
        matchedByLabel="ID Number"
        extraNote="Vehicles are auto-linked when the Registration matches an existing vehicle in your fleet."
      />

      <MergeDuplicateDriversDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        onMerged={fetchDrivers}
      />
    </div>
  );
};

export default DriversPage;
